import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringFrequency,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToWindowEdges} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, {useCallback, useMemo, useRef} from 'react';
import {Virtuoso, VirtuosoHandle} from 'react-virtuoso';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {WorkspacePanelDragOverlay} from '../Panel/WorkspacePanel/WorkspacePanelDragOverlay';
import {customCollisionDetection} from './customCollisionDetection';
import {useScrollToStableSectionId} from './PanelBankSectionsScrollContextProvider';
import {PanelCrossSectionDropIndicator} from './PanelCrossSectionDropIndicator';
import {SortablePanelBankSectionContainer} from './SortablePanelBankSection';
import {SectionWithRef} from './types';
import {useHandleDrag} from './useHandleDrag';
import {useWorkspaceSections} from './useWorkspaceSections';
import {getDndSortableSectionId} from './utils';
import {virtuosoComponents, VirtuosoContext} from './virtuosoComponents';
import {WorkspaceSectionDragOverlay} from './WorkspaceSectionDragOverlay';

const DND_MODIFIERS = [restrictToWindowEdges];
const DND_MEASURING_STRATEGY = {
  droppable: {
    strategy: MeasuringStrategy.BeforeDragging,
    frequency: MeasuringFrequency.Optimized,
  },
};

/**
 * This component is currently being built in parallel to the existing PanelBankSections component. This is currently behind a statsig gate.
 * https://console.statsig.com/2qsBpsviQSxHWKIyMZHZzm/gates?Tag=2TAfQEBRDyRI5yekkM1kA
 */
export const PanelBankSections = ({
  isProjectReadOnly,
}: {
  isProjectReadOnly: boolean;
}) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  // TODO - add data dog logging

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // This determines what sections should be displayed in the workspace
  // with search and filters applied. It is important to filter out sections
  // that are empty or should not be visible, so we can take advantages of
  // virtualization. https://virtuoso.dev/troubleshooting/#i-get-error-zero-sized-element-this-should-not-happen
  const visibleSections = useWorkspaceSections();

  const items = useMemo(() => {
    return visibleSections.map(s => getDndSortableSectionId(s.ref));
  }, [visibleSections]);

  const visibleRangeRef = useRef<{
    startIndex: number;
    endIndex: number;
  } | null>(null);

  const {
    activeItem,
    handleDragEnd,
    handleDragOver,
    handleDragReset,
    handleDragStart,
    panelDropIndicatorRef,
    shouldDisplaySectionContent,
  } = useHandleDrag({
    visibleSections,
    virtuosoRef,
    visibleRangeRef,
  });

  const handleRangeChanged = useCallback(
    (range: {startIndex: number; endIndex: number}) => {
      visibleRangeRef.current = range;
    },
    []
  );

  const {debouncedSearchRegex} = usePanelSearchDebouncedContext();
  const virtuosoContext: VirtuosoContext = useMemo(() => {
    return {
      isSearching: !!debouncedSearchRegex,
      numVisibleSections: visibleSections.length,
      isProjectReadOnly,
      shouldDisplaySectionContent,
    };
  }, [
    debouncedSearchRegex,
    isProjectReadOnly,
    shouldDisplaySectionContent,
    visibleSections.length,
  ]);

  const computeItemKey = useCallback(
    (idx: number, sectionWithRef: SectionWithRef) => {
      return getDndSortableSectionId(sectionWithRef.ref);
    },
    []
  );

  // If we're coming from a full screen panel, we need to scroll to the section that was active when we left the full screen panel.
  useScrollToStableSectionId(virtuosoRef, visibleSections);

  return (
    // We use the default  "closest corners" collision detection since it's more aligned with
    // what the human eye would predict since the drag handle is in the top left corner.
    <DndContext
      autoScroll={{
        // Disabling this allows a smoother section drop experience since we collapse sections while dragging and reopen when drag ends.
        layoutShiftCompensation: false,
        enabled: true,
      }}
      collisionDetection={customCollisionDetection}
      measuring={DND_MEASURING_STRATEGY}
      modifiers={DND_MODIFIERS}
      onDragAbort={handleDragReset}
      onDragCancel={handleDragReset}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      sensors={sensors}>
      {/*
       * We want to render the panel cross section drop indicator and hide it when it's not needed.
       * This is to prevent unnecessary re-renders and optimize for performance.
       */}
      <PanelCrossSectionDropIndicator ref={panelDropIndicatorRef} />
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Virtuoso
          components={virtuosoComponents}
          computeItemKey={computeItemKey}
          context={virtuosoContext}
          data={visibleSections}
          itemContent={VirtuosoItemContent}
          rangeChanged={handleRangeChanged}
          ref={virtuosoRef}
          className="grow bg-moon-50"
          useWindowScroll
        />
      </SortableContext>
      {/*
       * The <DragOverlay> component should remain mounted at all times so that it can perform the drop animation.
       * If you conditionally render the <DragOverlay> component, drop animations will not work.
       * Setting dropAnimation to null for a smoother drop experience
       * https://docs.dndkit.com/api-documentation/draggable/drag-overlay#drop-animation
       */}
      <DragOverlay dropAnimation={null}>
        <WorkspaceSectionDragOverlay activeItem={activeItem} />
        <WorkspacePanelDragOverlay activeItem={activeItem} />
      </DragOverlay>
    </DndContext>
  );
};

/**
 * This is intentionally pulled out as a separate function to avoid unnecessary re-renders
 * when the parent component updates. When defined inline, React would recreate this function
 * on every render, causing Virtuoso to re-render all visible items even when their data hasn't changed.
 *
 * https://virtuoso.dev/#performance
 *
 */
const VirtuosoItemContent = (
  idx: number,
  sectionWithRef: SectionWithRef,
  context: VirtuosoContext
) => {
  // Do not add any hooks here, otherwise you'll get "Rendered more hooks than during the previous render."
  return (
    <SortablePanelBankSectionContainer
      idx={idx}
      isProjectReadOnly={context.isProjectReadOnly}
      numVisibleSections={context.numVisibleSections}
      sectionWithRef={sectionWithRef}
      shouldDisplaySectionContent={context.shouldDisplaySectionContent}
    />
  );
};
